/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 09/11/2024 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 08/10/2026 | LIC. José Luis Aragón C.                       | Cabecero primary.main (igual que
 *              |            |                                  | demás buscadores) + avance visual:
 *              |            |                                  | foco + scroll al renglón
 *              |            |                                  | seleccionado (patrón Ventas)
 * --------------------------------------------------------------------------------------- */

import TextField from "../ui/TextField";
import { Fragment, useEffect, useState, forwardRef, useRef, useMemo } from "react";

import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, Paper, Box, Grid, Button } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PropTypes from "prop-types";
import { esES } from "@mui/material/locale";
import { useConfiguracion } from "../../context/System/ConfiguracionContext";
import getObjectValue from "lodash.get";
import { isNullOrEmpty } from "../../settings/utils";

const SizeSoftXTablePaginadoFilter = forwardRef((props, ref) => {
  const {
    data = [],
    columns,
    disableStickyHeader,
    totalDeRegistros = 0,
    setRow,
    defaultRow,
    row,
    id,
    fontSize = "medium",
    rowSpacing = 2,
    rowsPerPageSet,
    EspaciadorColumnas = 12,
    pageNow = 0,
    // Props para paginación externa (servidor)
    onPageChangeExt,
    onRowsPerPageChangeExt,
    // Prop para manejar doble click en filas
    onRowDoubleClick,
    maxHeight,
    // Desactiva la paginación y muestra todos los registros en una sola lista con scroll
    disablePagination = false,
  } = props;

  const pager = [3, 4, 5, 6, 9, 10, 15, 20, 50, 100];
  const theme = createTheme(esES);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const isExternalChange = useRef(false);
  const [dataRows, setDataRows] = useState(Array.isArray(data) ? data : []);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState(true);

  const [dataBase, setDataBase] = useState(Array.isArray(data) ? data : []);
  const [dataView, setDataView] = useState(Array.isArray(data) ? data : []);
  const [filters, setFilters] = useState({});
  const [navigationDirection, setNavigationDirection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const isPageChangeFromArrow = useRef(false);
  // Rev 2: contenedor propio para el avance visual (foco + scroll al
  // seleccionado). Se fusiona con el ref reenviado al TableContainer.
  const containerRef = useRef(null);
  const mergeContainerRef = (node) => {
    containerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const { setPaginadoModel, getPaginadoModel, paginadoModel } =
    useConfiguracion();

  useEffect(() => {
    setPaginadoModel(getPaginadoModel());
  }, []);

  useEffect(() => {
    setRowsPerPage(rowsPerPageSet);
  }, [rowsPerPageSet]);

  // Sincronizar con pageNow externo (cuando el servidor devuelve nuevos datos)
  useEffect(() => {
    if (pageNow >= 0 && pageNow !== page && !isPageChangeFromArrow.current) {
      setPage(pageNow);
    }
  }, [pageNow]);

  const getMaxWidthForColumn = (column) => {
    if (!Array.isArray(dataRows)) return column.width || 100;
    const maxDataWidth = dataRows.reduce((maxWidth, row) => {
      const cellValue = getObjectValue(row, column.field) || "";
      return Math.max(
        maxWidth,
        cellValue.toString().length * EspaciadorColumnas
      );
    }, 0);
    const contentWidth = maxDataWidth > 100 ? maxDataWidth : 100;
    return column.width ? Math.max(contentWidth, column.width) : contentWidth;
  };

  const minTableWidth = useMemo(() => {
    return columns.reduce((acc, col) => acc + (col.width || 150), 0);
  }, [columns]);

  const handleChangePage = async (e, newPage) => {
    if (isPageChangeFromArrow.current) {
      isPageChangeFromArrow.current = false;
      return;
    }
    if (newPage === page) {
      return;
    }
    paginadoModel.pagina.value = newPage + 1;
    setPage(newPage);
    if (onPageChangeExt) {
      onPageChangeExt(newPage + 1);
    }
  };

  const handleChangeRowsPerPage = async (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    isExternalChange.current = false;
    // Si hay callback externo, llamarlo (paginación servidor)
    if (onRowsPerPageChangeExt) {
      onRowsPerPageChangeExt(newRowsPerPage);
    }
  };

  const handleClickRow = (valueRow) => () => {
    if (!defaultRow) {
      setRow(valueRow);
      return;
    }
    if (!row) {
      setRow(valueRow);
      return;
    }
    if (valueRow[id] === row[id]) {
      setRow(defaultRow);
      return;
    }
    setRow(valueRow);
  };

  const handleClickOrder = (field, param = false) => {
    const orderField = field === orderBy ? (param ? order : !order) : order;

    const ordered = handleGetListOrden(dataView, field, orderField);

    setDataView(ordered);
    setOrder(orderField);

    if (!isNullOrEmpty(field)) setOrderBy(field);

    const Sorder = {
      ...paginadoModel,
      sByOrder: {
        ...paginadoModel.sByOrder,
        value: orderField === false ? "Desc" : "Asc",
      },
      sField: { ...paginadoModel.sField, value: field },
    };
    setPaginadoModel(Sorder);
  };

  const handleGetListOrden = (dataList, fieldFilter, orderField) => {
    const list = Array.isArray(dataList) ? [...dataList] : [];

    const orderList = orderField
      ? list.sort((a, b) => {
          const valueA = getObjectValue(a, fieldFilter);
          const valueB = getObjectValue(b, fieldFilter);
          if (typeof valueA === "string" && typeof valueB === "string") {
            return valueA.toLowerCase() < valueB.toLowerCase() ? -1 : 1;
          } else {
            return valueA < valueB ? -1 : 1;
          }
        })
      : list.sort((a, b) => {
          const valueA = getObjectValue(a, fieldFilter);
          const valueB = getObjectValue(b, fieldFilter);
          if (typeof valueA === "string" && typeof valueB === "string") {
            return valueA.toLowerCase() < valueB.toLowerCase() ? 1 : -1;
          } else {
            return valueA < valueB ? 1 : -1;
          }
        });

    return orderList;
  };

  useEffect(() => {
    if (disablePagination) {
      setDataRows(dataView);
      return;
    }
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    setDataRows(dataView.slice(startIndex, endIndex));
  }, [dataView, page, rowsPerPage, disablePagination]);

  useEffect(() => {
    if (orderBy) {
      handleClickOrder(orderBy, true);
    }
  }, [data]);

  useEffect(() => {
    if (!isPageChangeFromArrow.current && !isLoading) {
      setPage(0);
    }
  }, [totalDeRegistros]);

  // --- Navegación con flechas ---
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!dataRows.length || isLoading) return;
      const currentIndex = dataRows.findIndex((r) => r[id] === row?.[id]);
      const totalPages = Math.ceil(totalDeRegistros / rowsPerPage);

      if (event.key === "ArrowDown") {
        if (currentIndex === -1) {
          setRow(dataRows[0]);
          return;
        }

        // Si no es el último registro de la pégina actual
        if (currentIndex < dataRows.length - 1) {
          setRow(dataRows[currentIndex + 1]);
        }
        // Si es el último registro de la pégina actual y hay més péginas
        else if (!disablePagination && page < totalPages - 1) {
          const nextPage = page + 1;
          isPageChangeFromArrow.current = true;
          setNavigationDirection('next');
          setIsLoading(true);
          setPage(nextPage);
          paginadoModel.pagina.value = nextPage + 1;
          if (onPageChangeExt) {
            onPageChangeExt(nextPage + 1);
          }
        }
        // Si es el último registro de la última pégina, quedarse
      }

      if (event.key === "ArrowUp") {
        if (currentIndex === -1) {
          setRow(dataRows[0]);
          return;
        }

        // Si no es el primer registro de la pégina actual
        if (currentIndex > 0) {
          setRow(dataRows[currentIndex - 1]);
        }
        // Si es el primer registro de la pégina actual y no es la primera pégina
        else if (!disablePagination && page > 0) {
          const prevPage = page - 1;
          isPageChangeFromArrow.current = true;
          setNavigationDirection('prev');
          setIsLoading(true);
          setPage(prevPage);
          paginadoModel.pagina.value = prevPage + 1;
          if (onPageChangeExt) {
            onPageChangeExt(prevPage + 1);
          }
        }
        // Si es el primer registro de la primera pégina, quedarse
      }
    };

    if (ref?.current) {
      ref.current.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      if (ref?.current) {
        ref.current.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [dataRows, row, id, ref, page, rowsPerPage, totalDeRegistros, onPageChangeExt, setRow, disablePagination]);

  // Efecto para seleccionar primer/último registro al cambiar de pégina por navegación
  useEffect(() => {
    if (isLoading) return;
    if (navigationDirection === 'next' && dataRows.length > 0) {
      setRow(dataRows[0]);
      setNavigationDirection(null);
    } else if (navigationDirection === 'prev' && dataRows.length > 0) {
      setRow(dataRows[dataRows.length - 1]);
      setNavigationDirection(null);
    }
  }, [page, navigationDirection, dataRows, setRow, isLoading]);

  // Rev 2: avance visual del renglón seleccionado (foco + scroll dentro
  // del contenedor, sin mover la página). Patrón SizeSoftXTableVentas.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !row) return;
    const selected = container.querySelector("tr.row");
    if (!selected) return;
    try {
      selected.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {}
    try {
      if (document.activeElement && container.contains(document.activeElement)) {
        selected.focus({ preventScroll: true });
      }
    } catch {}
  }, [row, dataRows, page]);

  useEffect(() => {
    if (data && data.length > 0 && isLoading) {
      setIsLoading(false);
    }
  }, [data]);

  useEffect(() => {
    const base = Array.isArray(data) ? data : [];
    setDataBase(base);

    // 1) filtrar
    const filtered = applyFilters(base, filters);

    // 2) ordenar si ya hay orden
    const ordered = orderBy
      ? handleGetListOrden(filtered, orderBy, order)
      : filtered;

    setDataView(ordered);

    // si filtraste y te quedaste en pégina alta, regresa a la 0
    setPage(0);
  }, [data, filters]); // (order/orderBy se aplican también abajo cuando clickeas flecha)

  useEffect(() => {
    if (!row) return;
    const exists = dataView.some((r) => r[id] === row[id]);
    if (!exists) setRow(defaultRow || null);
  }, [dataView]);

  //Helpers..........
  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const applyFilters = (list, filtersObj) => {
    const entries = Object.entries(filtersObj || {}).filter(
      ([_, v]) => !isNullOrEmpty(v)
    );
    if (!entries.length) return list;

    return list.filter((row) => {
      return entries.every(([field, value]) => {
        const cell = normalize(getObjectValue(row, field));
        const needle = normalize(value);
        return cell.includes(needle);
      });
    });
  };
  //.....................................

  return (
    <Fragment>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid item xs={12}>
          <TableContainer
            ref={mergeContainerRef}
            component={Paper}
            tabIndex={0} // <-- Para permitir foco
            sx={{ outline: "none", overflow: 'auto', maxHeight }}
          >
            <Table stickyHeader={!disableStickyHeader} style={{ minWidth: minTableWidth }}>
              <TableHead>
                <TableRow>
                  {columns.map((column, i) => (
                    <TableCell
                      key={"f-" + i}
                      style={{
                        minWidth: getMaxWidthForColumn(column),
                        padding: "2px 8px",
                      }}
                      sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
                    >
                      {/* Solo si quieres filtrar esa columna */}
                      {column.disableFilter ? null : (
                        <TextField autoComplete="off"
                          variant="standard"
                          value={filters[column.field] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFilters((prev) => ({
                              ...prev,
                              [column.field]: val,
                            }));
                          }}
                          onKeyDown={(e) => e.stopPropagation()} // para no pelear con flechas/enter
                          placeholder="Filtrar..."
                          fullWidth
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            fontSize: "12px",
                            "& .MuiInputBase-input": { color: "primary.contrastText" },
                            "& .MuiInputBase-input::placeholder": { color: "primary.contrastText", opacity: 0.7 },
                          }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {columns.map((column, i) => (
                    <TableCell
                      key={i}
                      style={{
                        minWidth: getMaxWidthForColumn(column),
                        whiteSpace: "nowrap",
                        fontSize,
                        padding: "4px 8px",
                      }}
                      sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
                    >
                      {!column.hideSort && (
                        <Button
                          style={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textTransform: "none",
                            fontSize,
                          }}
                          variant="text"
                          color="inherit"
                          className={
                            orderBy === column.field ? "sort" : "no-sort"
                          }
                          onClick={() => handleClickOrder(column.field)}
                          startIcon={
                            orderBy === column.field ? (
                              order ? (
                                <ArrowUpwardIcon />
                              ) : (
                                <ArrowDownwardIcon />
                              )
                            ) : (
                              <ArrowUpwardIcon />
                            )
                          }
                        >
                          <Typography
                            variant="button"
                            sx={{ fontWeight: "600", color: "primary.contrastText" }}
                            style={{
                              fontSize,
                              margin: "none",
                              padding: "none",
                            }}
                          >
                            {column.title}
                          </Typography>
                        </Button>
                      )}
                      {column.showRenderHeader && column.renderHeader()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRows.length > 0 ? (
                  dataRows.map((dataRow, i) => (
                    <TableRow
                      key={i}
                      id={`row-${i}`}
                      tabIndex={-1}
                      onClick={handleClickRow(dataRow)}
                      onDoubleClick={() => onRowDoubleClick?.(dataRow)}
                      style={{ height: 6 + rowSpacing, fontSize, cursor: onRowDoubleClick ? 'pointer' : 'default', outline: "none" }}
                      className={
                        !row
                          ? "no-row"
                          : dataRow[id] === row[id]
                          ? "row"
                          : "no-row"
                      }
                    >
                      {columns.map((column, j) => (
                        <TableCell
                          key={j}
                          align={column.align || "left"}
                          style={{
                            fontSize,
                            padding: "4px 8px",
                            position: "relative",
                          }}
                        >
                          {!column.render ? (
                            <Typography>{dataRow[column.field]}</Typography>
                          ) : (
                            column.render(dataRow)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography>
                        No se encontraron registros para mostrar
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box display="flex" justifyContent="center">
              {!disablePagination && totalDeRegistros > 0 && (
                <TablePagination
                  component="div"
                  count={totalDeRegistros}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={pager}
                  labelRowsPerPage="Registros por pégina"
                />
              )}
            </Box>
          </TableContainer>
        </Grid>
      </Grid>
    </Fragment>
  );
});

SizeSoftXTablePaginadoFilter.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      title: PropTypes.string,
      render: PropTypes.func,
      hideSort: PropTypes.bool,
      width: PropTypes.number,
      align: PropTypes.string,
      showRenderHeader: PropTypes.bool,
    })
  ),
  data: PropTypes.array,
  id: PropTypes.string.isRequired,
  fontSize: PropTypes.oneOfType([PropTypes.oneOf(["small", "medium", "large"]), PropTypes.string]),
  rowSpacing: PropTypes.number,
  onRowDoubleClick: PropTypes.func,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disablePagination: PropTypes.bool,
};

export default SizeSoftXTablePaginadoFilter;
